<template>
  <div class="history-attr space-y-1">
    <div class="flex items-baseline gap-1 flex-wrap">
      <span v-if="labelHtml" class="font-semibold" v-html="labelHtml" />
      <span v-if="connector" class="text-gray-500">{{ connector }}</span>

      <template v-if="isStructured">
        <span class="text-gray-500 italic">{{ structuredSummary }}</span>
      </template>
      <template v-else-if="needsTruncate && !expanded">
        <span class="history-attr__value" v-html="truncatedValueHtml" />
        <span class="text-gray-400">…</span>
      </template>
      <template v-else>
        <span class="history-attr__value" v-html="valueHtml" />
      </template>

      <button
        v-if="canToggle"
        type="button"
        class="text-primary-500 hover:text-primary-700 text-xs underline ml-1 flex-shrink-0"
        @click="expanded = !expanded"
      >
        {{ expanded ? $t('general.showLess') : $t('general.showMore') }}
      </button>
    </div>

    <pre
      v-if="isStructured && expanded"
      class="text-xs bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded p-2 overflow-auto max-h-80 font-mono"
    >{{ formattedJson }}</pre>
  </div>
</template>

<script setup lang="ts">
const props = defineProps<{
  html: string
}>()

const expanded = ref(false)

const TRUNCATE_THRESHOLD = 200
const TRUNCATE_TO = 160

const parsed = computed(() => {
  const raw = props.html ?? ''
  const splitIdx = raw.indexOf(' changed to')
  if (splitIdx === -1) {
    return { labelHtml: '', connector: '', valueHtml: raw, json: null as unknown }
  }

  const labelHtml = raw.slice(0, splitIdx).trim()
  let rest = raw.slice(splitIdx + ' changed to'.length)
  const connector = 'changed to'
  const valueHtml = rest.replace(/^\s+/, '').trim()

  const valueText = stripTags(valueHtml).trim()
  let json: unknown = null
  if (
    (valueText.startsWith('{') && valueText.endsWith('}')) ||
    (valueText.startsWith('[') && valueText.endsWith(']'))
  ) {
    try {
      const candidate = JSON.parse(valueText)
      if (candidate && typeof candidate === 'object') json = candidate
    } catch { /* not JSON */ }
  }

  return { labelHtml, connector, valueHtml, json }
})

const labelHtml = computed(() => parsed.value.labelHtml)
const connector = computed(() => parsed.value.connector)
const valueHtml = computed(() => parsed.value.valueHtml)
const isStructured = computed(() => parsed.value.json !== null)

const valueText = computed(() => stripTags(valueHtml.value))
const needsTruncate = computed(() => !isStructured.value && valueText.value.length > TRUNCATE_THRESHOLD)
const canToggle = computed(() => needsTruncate.value || isStructured.value)

const truncatedValueHtml = computed(() => {
  const raw = valueHtml.value
  let seen = 0
  let inTag = false
  let out = ''
  for (let i = 0; i < raw.length && seen < TRUNCATE_TO; i++) {
    const ch = raw[i]
    out += ch
    if (ch === '<') inTag = true
    else if (ch === '>') inTag = false
    else if (!inTag) seen++
  }
  return out
})

const formattedJson = computed(() => {
  if (!isStructured.value) return ''
  try {
    return JSON.stringify(parsed.value.json, null, 2)
  } catch {
    return String(parsed.value.json)
  }
})

const structuredSummary = computed(() => {
  const json = parsed.value.json
  if (Array.isArray(json)) {
    return `[${json.length} ${json.length === 1 ? 'item' : 'items'}]`
  }
  if (json && typeof json === 'object') {
    const count = Object.keys(json as Record<string, unknown>).length
    return `{${count} ${count === 1 ? 'field' : 'fields'}}`
  }
  return ''
})

function stripTags(html: string): string {
  return html.replace(/<[^>]*>/g, '')
}
</script>

<style scoped>
.history-attr {
  min-width: 0;
  overflow-wrap: anywhere;
  word-break: break-word;
}
.history-attr__value {
  min-width: 0;
  overflow-wrap: anywhere;
  word-break: break-word;
  max-width: 100%;
}
</style>
